import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { memberBillingService } from '../clients/ApiClients';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import HomeSelector from '../Components/Custom/HomeSelector';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import { useMember } from '../Hooks/MemberContext';
import { billingErrorMessage } from '../utils/billingHelpers';
import { StatementPeriod, statementPeriods } from './statementPeriods';

interface Props {
  currentPage: string;
}

/**
 * A list of monthly statements.
 *
 * **Not front-porch's from/to date pickers.** Those are the manager
 * affordance this epic was asked to reconsider: a homeowner wants "my
 * August statement", not an arbitrary range. The list is derived from
 * the account's oldest ledger entry, which costs one
 * `?order=asc&per_page=1` read and needs no new endpoint.
 */
const StatementsPage: React.FC<Props> = ({ currentPage }) => {
  const { homes, selectedHome, loading: memberLoading } = useMember();
  const homeId = selectedHome?.home_id;

  const [periods, setPeriods] = useState<StatementPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!homeId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // The oldest entry, and only that one — its effective date is
      // where the list of months starts.
      const { data } = await memberBillingService.getMyLedger({
        homeId,
        page: 1,
        perPage: 1,
        order: 'asc',
      });
      const earliest = data.entries[0]?.effective_date ?? null;
      setPeriods(statementPeriods(earliest));
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't load your statements."));
      setPeriods(statementPeriods(null));
    } finally {
      setLoading(false);
    }
  }, [homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (memberLoading) return <LoadingSpinner />;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Link
        to="/account"
        className="text-sm text-sky-700 hover:underline dark:text-sky-400"
      >
        &larr; Back to your account
      </Link>

      <Heading className="mt-2">Statements</Heading>

      {homes.length === 0 || !homeId ? (
        <Text className="mt-2">
          Your account is not linked to a home yet. Please contact your
          association manager.
        </Text>
      ) : (
        <>
          <div className="mt-2">
            <HomeSelector />
          </div>

          <ErrorAlert className="mt-4" onDismiss={() => setError(null)}>
            {error}
          </ErrorAlert>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <ul className="mt-5 divide-y divide-zinc-200 overflow-hidden rounded-xl border border-zinc-200 bg-white dark:divide-zinc-700 dark:border-zinc-700 dark:bg-gray-800">
              {periods.map((period) => (
                <li key={period.id}>
                  <Link
                    to={`/account/statements/${period.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-4 hover:bg-zinc-50 dark:hover:bg-gray-700/50"
                  >
                    <span className="text-sm font-medium text-zinc-900 dark:text-white">
                      {period.label}
                    </span>
                    <ChevronRightIcon className="h-5 w-5 shrink-0 text-zinc-400" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </SidebarLayout>
  );
};

export default StatementsPage;
